import { Project } from "@app/project/model";
import { ActionPayloadGeneric, CHUuid } from "@app/root-store/actions";
import {
    createActionSet,
    createCrudActionSets,
    createDeleteAllPromptActionSet,
    createDeletePromptActionSet,
    createNoPropsAction,
    createRequestAction,
} from "@app/root-store/actions/action-creators";
import { CommandProviders } from "@app/rule/model/commands/command";
import { ConditionProviders } from "@app/rule/model/conditions/condition";
import { CHRuleInt } from "@app/rule/model/rule";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";

const actionCategory = "Rule";

/**
 * A payload used when performing an operation on many entities
 */
export type BulkActionPayload = ActionPayloadGeneric &
    CHUuid & {
        rules: CHRuleInt[];
    };
export type LoadActionPayload = CHUuid & ActionPayloadGeneric;
export type ActionPayload = LoadActionPayload &
    ActionPayloadGeneric & {
        rule: CHRuleInt;
        project?: Project;
    };
export type ActionResult = LoadActionPayload & {
    rule: CHRuleInt;
};
export type BulkActionResult = CHUuid & {
    rules: ClareHomeDbCollection<CHRuleInt>;
};

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
export const {
    load: loadSet,
    loadSingle: loadSingleSet,
    create: createSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);
export const {
    request: deleteAll,
    success: deleteAllSuccess,
    failure: deleteAllFailure,
} = createActionSet<BulkActionPayload>(actionCategory, "Bulk Delete");

export const clearRuleState = createNoPropsAction(actionCategory, "clear");

export const cancelActionStepAdd = createRequestAction<
    ActionPayloadGeneric & {
        provider: CommandProviders | ConditionProviders;
    }
>(actionCategory, "cancel step action");

/**
 * An action set to prompt a user when deleting an item
 */
export const { confirm: deleteConfirmation, dismiss: deleteConfirmationDismiss } =
    createDeletePromptActionSet<ActionPayload>(actionCategory);

export const deleteConfirmationSet = { ...deleteSet, deleteConfirmation, deleteConfirmationDismiss };

/**
 * An action set to prompt a user when deleting multiple items
 */
export const { confirm: deleteAllConfirmation, dismiss: deleteAllDismiss } =
    createDeleteAllPromptActionSet<BulkActionPayload>(actionCategory);
export const deleteAllConfirmationSet = {
    request: deleteAll,
    success: deleteAllSuccess,
    failure: deleteAllFailure,
    deleteAllConfirmation,
    deleteAllDismiss,
};
